import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client/react';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  TaskScopeNameEnum,
  useEdmDocumentationBulkEditMutation,
  useEdmDocumentationCategoriesSearchListLazyQuery,
  useEdmDocumentRenewalReminderTemplateQuery,
  useSectorsSearchListLazyQuery,
  useSitesSearchListLazyQuery,
  useUsersSearchListLazyQuery,
  useAssetsSearchListLazyQuery,
  useSiteAreasSearchListLazyQuery,
  useFormTemplatesSearchListLazyQuery,
  useTasksSearchListLazyQuery,
} from 'graphql-common';
import { FieldItem, Values } from '@lib/interfaces/form';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import { GetLoadOptions } from '@lib/components/Select/types';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { getDocumentationCategoriesLoadOptions } from 'utils/fetch/searchLists/documentationCategoriesSearchList';
import { getSectorsLoadOptions } from 'utils/fetch/searchLists/sectorsSearchList';
import { getSitesLoadOptions } from 'utils/fetch/searchLists/sitesSearchList';
import { getUsersLoadOptions } from 'utils/fetch/searchLists/usersSearchList';
import { getAssetsLoadOptions } from 'utils/fetch/searchLists/assetsSearchList';
import { getSiteAreasLoadOptions } from 'utils/fetch/searchLists/siteAreasSearchList';
import { getFormTemplatesLoadOptions } from 'utils/fetch/searchLists/formTemplatesSearchList';
import { getTasksLoadOptions } from 'utils/fetch/searchLists/tasksSearchList';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import createTaskGroupKeyResolver from '../../utils/createTaskGroupKeyResolver';
import getCategoryFields from '../../DocumentForm/utils/getCategoryFields';
import getAccessValidationFields from '../../DocumentForm/utils/getAccessValidationFields';
import getOperationalScopeFields from '../../DocumentForm/utils/getOperationalScopeFields';
import getLifecycleFields from '../../DocumentForm/utils/getLifecycleFields';
import prepareBulkEditData from './prepareBulkEditData';

interface UseDocumentBulkEditActionsProps {
  ids: string[];
  onCompleted: () => void;
  openEquipmentModal?: () => void;
  openAreasModal?: () => void;
  openTasksModal?: () => void;
}

export default function DocumentBulkEditActions({
  ids,
  onCompleted,
  openEquipmentModal,
  openAreasModal,
  openTasksModal,
}: UseDocumentBulkEditActionsProps) {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const getTaskGroupKey = useMemo(
    () => createTaskGroupKeyResolver(apolloClient),
    [apolloClient],
  );

  const { getUserAccessByCompany, getUserAccessByComponent }: ContextProps =
    useOutletContext();
  // Renewal-reminder feature is locked when EDM is restricted at either
  // company plan or user-role level; skip its template query in both cases.
  const isDocumentationLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead ||
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead;
  const hasCategoriesAccess =
    getUserAccessByComponent(
      AccessComponentEnum.DocumentationCategoriesManagement,
    ) !== AccessLevelEnum.LimitedRead;
  const hasSitesAccess =
    getUserAccessByComponent(AccessComponentEnum.SitesManagement) !==
    AccessLevelEnum.LimitedRead;
  const hasAssetsAccess =
    getUserAccessByComponent(AccessComponentEnum.AssetsManagement) !==
    AccessLevelEnum.LimitedRead;
  const hasTasksAccess =
    getUserAccessByComponent(AccessComponentEnum.TasksManagement) !==
    AccessLevelEnum.LimitedRead;
  const hasUsersAccess =
    getUserAccessByComponent(AccessComponentEnum.UsersManagement) !==
    AccessLevelEnum.LimitedRead;

  // Lazy queries
  const [fetchCategories] = useEdmDocumentationCategoriesSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchSectors] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchSites] = useSitesSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchUsers] = useUsersSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchAssets] = useAssetsSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchAreas] = useSiteAreasSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchFormTemplates] = useFormTemplatesSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [fetchTasks] = useTasksSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });

  const { data: reminderTemplateData } =
    useEdmDocumentRenewalReminderTemplateQuery({
      fetchPolicy: 'cache-and-network',
      skip: isDocumentationLocked,
    });
  const renewalReminderTemplate = reminderTemplateData?.data ?? null;

  // Load options
  const categoriesLoadOptions: GetLoadOptions = useCallback(
    () => getDocumentationCategoriesLoadOptions(fetchCategories),
    [fetchCategories],
  );
  const sectorsLoadOptions: GetLoadOptions = useCallback(
    () => getSectorsLoadOptions(fetchSectors),
    [fetchSectors],
  );
  const sitesLoadOptions: GetLoadOptions = useCallback(
    () => getSitesLoadOptions(fetchSites, undefined, { includeSector: true }),
    [fetchSites],
  );
  const approverLoadOptions = useCallback(
    (name, values) => {
      const categoryId = _get<string>(
        values,
        [DocumentFormFieldNames.Category, 'value'],
        '',
      );
      return getUsersLoadOptions(fetchUsers, {
        custom: {
          byCore: {
            onlyActive: true,
            access: {
              component: AccessComponentEnum.DocumentationManagement,
              level: AccessLevelEnum.Write,
              sourceableId: categoryId?.length ? categoryId : undefined,
            },
          },
        },
      });
    },
    [fetchUsers],
  );
  const equipmentLoadOptions: GetLoadOptions = useCallback(
    () => getAssetsLoadOptions(fetchAssets),
    [fetchAssets],
  );
  const areasLoadOptions: GetLoadOptions = useCallback(
    () => getSiteAreasLoadOptions(fetchAreas),
    [fetchAreas],
  );
  const formTemplatesLoadOptions: GetLoadOptions = useCallback(
    () => getFormTemplatesLoadOptions(fetchFormTemplates),
    [fetchFormTemplates],
  );
  const tasksLoadOptions: GetLoadOptions = useCallback(
    () =>
      getTasksLoadOptions({
        fetchLazyQuery: fetchTasks,
        scope: { name: TaskScopeNameEnum.Active },
        selectedSiteIds: [],
      }),
    [fetchTasks],
  );

  // Form fields
  const formFields: FieldItem[] = useMemo(() => {
    const categoryFields = getCategoryFields({
      t,
      categoriesLoadOptions,
      showTitle: true,
      hasCategoriesAccess,
      withPermissionsFields: true,
    });
    const accessValidationFields = getAccessValidationFields({
      t,
      sectorsLoadOptions,
      sitesLoadOptions,
      approverLoadOptions,
      hasSitesAccess,
      hasUsersAccess,
    });
    const operationalScopeFields = getOperationalScopeFields({
      t,
      equipmentLoadOptions,
      areasLoadOptions,
      formTemplatesLoadOptions,
      tasksLoadOptions,
      openEquipmentModal,
      openAreasModal,
      openTasksModal,
      hasAssetsAccess,
      hasSitesAccess,
      hasTasksAccess,
    });
    const precedingFieldsCount =
      categoryFields.length +
      accessValidationFields.length +
      operationalScopeFields.length;
    const lifecycleFields = getLifecycleFields({
      t,
      template: renewalReminderTemplate,
      hasPriorReminderValues: false,
      isBlockStart: precedingFieldsCount > 0,
    });
    return [
      ...categoryFields,
      ...accessValidationFields,
      ...operationalScopeFields,
      ...lifecycleFields,
    ];
  }, [
    t,
    categoriesLoadOptions,
    sectorsLoadOptions,
    sitesLoadOptions,
    approverLoadOptions,
    equipmentLoadOptions,
    areasLoadOptions,
    formTemplatesLoadOptions,
    tasksLoadOptions,
    openEquipmentModal,
    openAreasModal,
    openTasksModal,
    renewalReminderTemplate,
    hasCategoriesAccess,
    hasSitesAccess,
    hasAssetsAccess,
    hasTasksAccess,
    hasUsersAccess,
  ]);

  // Mutation
  const [bulkEditMutation, { loading }] = useEdmDocumentationBulkEditMutation({
    onError: (error) => showGraphQLErrors(error, t),
  });

  const onSubmit = useCallback(
    (values: Values) => {
      if (!ids.length) return;
      const attributes = prepareBulkEditData(values, getTaskGroupKey);
      if (Object.keys(attributes).length === 0) return;
      bulkEditMutation({
        variables: {
          ids,
          attributes,
        },
        onCompleted: () => {
          toast({ content: t('update-document-success') });
          onCompleted();
        },
      });
    },
    [bulkEditMutation, getTaskGroupKey, ids, onCompleted, t],
  );

  return {
    formFields,
    loading,
    onSubmit,
  };
}
